import { StudentType } from '@wonderschool/common-base-types';
import { firestore } from '../api/firebase/firebase';
import { batchSaveDocuments, saveDocument } from '../api/firebase/firestore';
import { logError } from '../rollbar';
import { EnrollmentStatusEnum } from './enums';
import { EnrollmentDeposit } from './types';

const paths = {
  rootStudents: () => `students`,
  students: (organizationId: string) => `organizations/${organizationId}/students`,
  contacts: (organizationId: string) => `organizations/${organizationId}/contacts`,
};

export async function saveStudentAndContacts({
  organization,
  student,
  contacts,
}: {
  organization: any;
  student: any;
  contacts: any[];
}) {
  const batch = firestore().batch() as any;

  try {
    await saveDocument({ collectionPath: paths.students(organization.id), data: student, batch });

    for (const contact of contacts) {
      await saveDocument({ collectionPath: paths.contacts(organization.id), data: contact, batch });
    }
    await batch.commit();
  } catch (error) {
    logError('Error saving student and contacts', { studentId: student.id, error });
  }
}

type DocumentsReturnType = {
  count: number;
  list: any[];
  lastDocument: any | null;
  hasMoreData: boolean;
};

type FetchStudentsToEnrollParams = {
  organizationId?: string;
  targetDate?: Date;
};

const _empty_docs = {
  count: 0,
  list: [],
  lastDocument: null,
  hasMoreData: false,
};

export async function fetchStudentsToEnroll({
  organizationId: _orgId,
  targetDate = new Date(),
}: FetchStudentsToEnrollParams): Promise<DocumentsReturnType> {
  const snapshot = await firestore()
    .collection(paths.rootStudents())
    .where('enrollment.status', '==', EnrollmentStatusEnum.PENDING)
    .where('enrollment.automaticallyEnroll', '==', true)
    .where('enrollment.startDate', '<=', targetDate)
    .get();
  return {
    count: snapshot.size,
    list: snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
    lastDocument: snapshot.docs[snapshot.docs.length - 1],
    hasMoreData: false,
  } as DocumentsReturnType;
}

export async function addInvoiceToStudent(
  student: StudentType,
  tuitionAndFees: { tuition: any; fees: any[] },
  organizationId: string
) {
  const batch = firestore().batch();

  try {
    // Promote any deposit/registration-fee entry in fees[] into the
    // structured `enrollment.deposit` field. The composer doesn't have
    // its own deposit picker — directors just choose the labeled fee
    // from the one-time fees dropdown — so this is where the structured
    // record gets created. Idempotency: if the student already has a
    // live `enrollment.deposit` (e.g. they're being re-saved with the
    // same fees), we leave it alone so we don't clobber Stripe-linked
    // fields like `paymentIntentId` / `paidAt` that get set after
    // checkout.
    const existingDeposit: EnrollmentDeposit | undefined = (student as any)?.enrollment?.deposit;
    const isDepositLive = existingDeposit && !existingDeposit.settledAt && !existingDeposit.refundedAt;
    const derivedDeposit = isDepositLive ? null : deriveDepositFromFees(tuitionAndFees.fees);

    // Only the enrollment: a full copy of this older student could undo a family fix made since.
    const dataToSave = {
      id: student.id,
      enrollment: {
        tuitionAndFees,
        ...(derivedDeposit && { deposit: derivedDeposit }),
      },
    };

    await saveDocument({ collectionPath: paths.students(organizationId), data: dataToSave, batch });
    await batch.commit();
  } catch (error) {
    logError('Error saving student and contacts', { studentId: student.id, error });
  }
}

export async function enrollStudentsNow(students: any[], user: any) {
  try {
    const now = new Date();
    const dataToSave = students.map((student) => {
      return {
        id: student.id,
        organization: student.organization,
        enrollmentStatus: true,
        enrollmentDate: now.valueOf(),
        enrollment: {
          status: EnrollmentStatusEnum.ENROLLED,
          enrolled: {
            date: now,
            initiatedBy: user.uid ?? user.id ?? 'unknown',
            notes: 'Automatically enrolled',
          },
        },
      };
    });
    await batchSaveDocuments({
      documents: dataToSave,
      collectionPath: (student: any) => {
        return paths.students(student.organization);
      },
    });
  } catch (error) {
    logError('Error saving students', { error });
  }
}

// ---------------------------------------------------------------------------
// Deposits — port of the lego `Lego.Finance.{Deposit,CreateDeposit,
// DestroyDeposit,DepositWriteLock}` pattern to Firestore-doc shapes.
//
// Lego's design (ws-common-main/apps/nova/legacy/apps/lego/lib/lego/
// services/finance/deposit/) was:
//
//   1. `Deposit` row links enrollment ↔ transaction with a
//      `destroyed_at` soft-delete column. Never hard-deleted —
//      provides a permanent audit trail for state inspectors.
//   2. `CreateDeposit.run/1` acquires a per-enrollment write lock,
//      creates the transaction with a `type: "deposit"` line item,
//      then charges it via Stripe. Rolls back the transaction on
//      charge failure so you never end up with an orphan Deposit row.
//   3. `DestroyDeposit.run/1` sets `destroyed_at` (soft delete). The
//      actual refund cascade lives in `DestroyTransaction`.
//   4. Two-typed taxonomy: `Deposit` (refundable) vs `EnrollmentFee`
//      (non-refundable) — same shape, different semantics. We
//      collapse both into one struct keyed by `refundable: boolean`.
//
// ---------------------------------------------------------------------------

const DEPOSIT_NAME_REGEX = /deposit/i;
const NON_REFUNDABLE_REGEX = /non-?refundable/i;
// "Enrollment/Registration fee (non-refundable)" — lego's EnrollmentFee
// table, no "deposit" in the label. We still collapse it into the
// EnrollmentDeposit struct with `refundable: false`.
const ENROLLMENT_FEE_NAME_REGEX = /(enrollment|registration).*fee/i;

/**
 * Read the deposit on file for a student. Prefers the structured
 * `enrollment.deposit` field; falls back to scanning
 * `enrollment.tuitionAndFees.fees[]` for a name matching `/deposit/i`
 * (the legacy storage shape).
 *
 * Returns null when no deposit is held. Settled deposits are still
 * returned — callers that need to skip them check `deposit.settledAt`.
 */
export function readDeposit(student: any): EnrollmentDeposit | null {
  if (!student?.enrollment) return null;

  // Preferred: structured field on the enrollment doc.
  const structured: EnrollmentDeposit | undefined = student.enrollment.deposit;
  if (structured && typeof structured.amountCents === 'number') {
    return structured;
  }

  // Fallback: scan tuitionAndFees.fees[] for the legacy free-text shape.
  // Two patterns count: anything with "deposit" in it (refundable by
  // default unless the name flips it), or "Enrollment/Registration fee
  // (non-refundable)" which carries the same semantic role but lacks
  // the word "deposit" in its label. Sum amounts so multiple matching
  // entries (rare but possible) are combined.
  const fees: Array<{ name?: string; amount?: number }> = student.enrollment?.tuitionAndFees?.fees ?? [];

  const matches = fees.filter((f) => isDepositLikeFee(f.name));
  if (matches.length === 0) return null;

  const amountCents = matches.reduce((sum, f) => sum + (f.amount || 0), 0);
  if (amountCents <= 0) return null;

  // Non-refundable wins if any matched entry says so OR the entry is
  // an enrollment/registration fee (those are always non-refundable
  // in the legacy moxit data).
  const refundable = matches.every(
    (f) => !NON_REFUNDABLE_REGEX.test(f.name || '') && !ENROLLMENT_FEE_NAME_REGEX.test(f.name || '')
  );

  return {
    amountCents,
    refundable,
  };
}

/**
 * Whether a fee entry's name puts it under the deposit/registration-fee
 * umbrella — i.e. it should be promoted to the structured
 * `enrollment.deposit` field rather than left as a free-text fee line.
 */
function isDepositLikeFee(name?: string): boolean {
  if (!name) return false;
  return DEPOSIT_NAME_REGEX.test(name) || ENROLLMENT_FEE_NAME_REGEX.test(name);
}

/**
 * Build an EnrollmentDeposit from a `tuitionAndFees.fees[]` array.
 *
 * Used by the enrollment composer at save time: when a director picks
 * "Enrollment/Registration deposit (refundable)" or
 * "Enrollment/Registration fee (non-refundable)" from the one-time
 * fees dropdown, we still write the entry to `fees[]` (so the invoice
 * flow keeps working) but we also derive the structured deposit
 * record from it so the refund cron + parent-facing math have a
 * queryable shape to read from.
 *
 * Returns null if no deposit-like fee was found.
 */
export function deriveDepositFromFees(
  fees: Array<{ name?: string; amount?: number }> | undefined
): EnrollmentDeposit | null {
  if (!fees || fees.length === 0) return null;

  const matches = fees.filter((f) => isDepositLikeFee(f.name));
  if (matches.length === 0) return null;

  const amountCents = matches.reduce((sum, f) => sum + (f.amount || 0), 0);
  if (amountCents <= 0) return null;

  const refundable = matches.every(
    (f) => !NON_REFUNDABLE_REGEX.test(f.name || '') && !ENROLLMENT_FEE_NAME_REGEX.test(f.name || '')
  );

  return {
    amountCents,
    refundable,
  };
}

/**
 * Resolve the net deposit amount (cents) after applying any
 * director-set adjustment. Mirrors lego's
 * `deposit_amount + deposit_adjustment` semantics.
 */
export function depositNetCents(deposit: EnrollmentDeposit | null): number {
  if (!deposit) return 0;
  const base = deposit.amountCents || 0;
  const adj = deposit.adjustmentCents || 0;
  return Math.max(0, base + adj);
}

type CreateDepositParams = {
  organizationId: string;
  studentId: string;
  amountCents: number;
  refundable?: boolean;
  adjustmentCents?: number;
  paymentIntentId?: string;
};

/**
 * Stage a deposit on the enrollment doc. Mirrors
 * `Lego.Finance.CreateDeposit.run/1` minus the actual Stripe charge
 * (caller is responsible for kicking off Stripe; we just record the
 * intent + the resulting PaymentIntent id once the charge clears).
 *
 * The write is idempotent: if a deposit already exists, this rejects
 * with `:deposit_exists` so we don't overwrite a live record.
 *
 * In a transaction so the read-then-write race is closed (Firestore's
 * answer to lego's `DepositWriteLock`).
 */
export async function createDeposit({
  organizationId,
  studentId,
  amountCents,
  refundable = true,
  adjustmentCents = 0,
  paymentIntentId,
}: CreateDepositParams): Promise<{ ok: true; deposit: EnrollmentDeposit } | { ok: false; error: string }> {
  if (!organizationId || !studentId) {
    return { ok: false, error: 'missing_org_or_student' };
  }
  if (!Number.isFinite(amountCents) || amountCents <= 0) {
    return { ok: false, error: 'invalid_amount' };
  }

  const docRef = firestore().collection(paths.students(organizationId)).doc(studentId);

  try {
    const result = await firestore().runTransaction(async (tx) => {
      const snap = await tx.get(docRef);
      if (!snap.exists) return { ok: false as const, error: 'student_not_found' };

      const data: any = snap.data() || {};
      const existing: EnrollmentDeposit | undefined = data?.enrollment?.deposit;
      if (existing && !existing.settledAt && !existing.refundedAt) {
        return { ok: false as const, error: 'deposit_exists' };
      }

      const deposit: EnrollmentDeposit = {
        amountCents,
        adjustmentCents,
        refundable,
        paidAt: paymentIntentId ? new Date() : undefined,
        paymentIntentId,
      };

      tx.update(docRef, { 'enrollment.deposit': deposit });
      return { ok: true as const, deposit };
    });

    return result;
  } catch (error) {
    logError('createDeposit error', { organizationId, studentId, error });
    return { ok: false, error: 'transaction_failed' };
  }
}
